/**
 * What a room holds beyond its fabric (migration 0057): its extract fans and
 * cooker hoods, and the spaces where a radiator can go.
 *
 * EXTRACT. A fan that runs CONTINUOUSLY takes air out of the room all the
 * time, and the same volume comes in from outside to be heated: when that is
 * more than the room's air changes, it sets the room's ventilation loss. A fan
 * that only runs on demand (a light-switched bathroom fan, a cooker hood used
 * while cooking) does not — it is recorded, and says so.
 *
 * RADIATOR SPACES. Width and height, mm, per position — under the window, on
 * the chimney breast — so the portal can say the largest radiator that fits
 * and the smallest that keeps the room warm.
 */

export const EXTRACT_DEVICE_KINDS = ["extract-fan", "cooker-hood", "other"] as const;
export type ExtractDeviceKind = (typeof EXTRACT_DEVICE_KINDS)[number];

export const EXTRACT_DEVICE_LABELS: Readonly<Record<ExtractDeviceKind, string>> = {
  "extract-fan": "Extract fan",
  "cooker-hood": "Cooker hood",
  other: "Other extract",
};

export interface ExtractDevice {
  readonly kind: ExtractDeviceKind;
  /** Its air flow, m³/h — from the fan's label or datasheet. */
  readonly flowM3h: number;
  /** Runs all the time (a continuous-running or dMEV fan), not just on demand. */
  readonly continuous: boolean;
  readonly label?: string;
}

export interface RadiatorSpace {
  /** Where: "Under the window", "Chimney breast". */
  readonly label?: string;
  readonly widthMm: number;
  readonly heightMm: number;
}

export const ROOM_EXTRAS_LIMITS = {
  devices: 10,
  spaces: 6,
  flowM3h: { min: 1, max: 2000 },
  widthMm: { min: 200, max: 6000 },
  heightMm: { min: 150, max: 2500 },
  label: 80,
} as const;

/** The air a room's continuous extract takes out, m³/h. */
export function continuousExtractM3h(devices: readonly ExtractDevice[]): number {
  return devices.filter((d) => d.continuous).reduce((sum, d) => sum + d.flowM3h, 0);
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

function onlyKeys(v: Record<string, unknown>, keys: readonly string[]): string | null {
  const stray = Object.keys(v).find((k) => !keys.includes(k));
  return stray ? `has "${stray}", which it does not take` : null;
}

function labelProblem(v: Record<string, unknown>): string | null {
  const label = v["label"];
  if (label === undefined) return null;
  return typeof label === "string" && label.length <= ROOM_EXTRAS_LIMITS.label ? null : `label must be text of at most ${ROOM_EXTRAS_LIMITS.label} characters`;
}

/** Why `value` is not a list of extract devices, or null when it is. */
export function extractDevicesProblem(value: unknown): string | null {
  if (!Array.isArray(value)) return "must be a list";
  if (value.length > ROOM_EXTRAS_LIMITS.devices) return `may hold at most ${ROOM_EXTRAS_LIMITS.devices}`;
  for (const [i, d] of value.entries()) {
    if (!isObject(d)) return `[${i}] must be a device`;
    const problem =
      onlyKeys(d, ["kind", "flowM3h", "continuous", "label"]) ??
      (typeof d["kind"] === "string" && (EXTRACT_DEVICE_KINDS as readonly string[]).includes(d["kind"])
        ? null
        : `kind must be one of ${EXTRACT_DEVICE_KINDS.join(", ")}`) ??
      (typeof d["flowM3h"] === "number" &&
      d["flowM3h"] >= ROOM_EXTRAS_LIMITS.flowM3h.min &&
      d["flowM3h"] <= ROOM_EXTRAS_LIMITS.flowM3h.max
        ? null
        : `flowM3h must be ${ROOM_EXTRAS_LIMITS.flowM3h.min} to ${ROOM_EXTRAS_LIMITS.flowM3h.max} m³/h`) ??
      (typeof d["continuous"] === "boolean" ? null : "continuous must be true or false") ??
      labelProblem(d);
    if (problem) return `[${i}] ${problem}`;
  }
  return null;
}

/** Why `value` is not a list of radiator spaces, or null when it is. */
export function radiatorSpacesProblem(value: unknown): string | null {
  if (!Array.isArray(value)) return "must be a list";
  if (value.length > ROOM_EXTRAS_LIMITS.spaces) return `may hold at most ${ROOM_EXTRAS_LIMITS.spaces}`;
  const { widthMm, heightMm } = ROOM_EXTRAS_LIMITS;
  for (const [i, s] of value.entries()) {
    if (!isObject(s)) return `[${i}] must be a space`;
    const problem =
      onlyKeys(s, ["widthMm", "heightMm", "label"]) ??
      (typeof s["widthMm"] === "number" && s["widthMm"] >= widthMm.min && s["widthMm"] <= widthMm.max
        ? null
        : `widthMm must be ${widthMm.min} to ${widthMm.max} mm`) ??
      (typeof s["heightMm"] === "number" && s["heightMm"] >= heightMm.min && s["heightMm"] <= heightMm.max
        ? null
        : `heightMm must be ${heightMm.min} to ${heightMm.max} mm`) ??
      labelProblem(s);
    if (problem) return `[${i}] ${problem}`;
  }
  return null;
}
